import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef, useState, type TouchEvent } from 'react'
import { store, type ConfiguratorTabId } from '../../../stores/store'
import EditorTabSlider, { type EditorTab } from '../EditorTabSlider'
import ColorButton from '../buttons/ColorButton'
import ButtonCurved from '../buttons/ButtonCurved'
import EditorButton from '../buttons/EditorButton'
import clsx from 'clsx'
import { GetGameDocument } from '@/src/lib/graphql/generated/graphql'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import {
    CHAMELEON_TEXTURE_PRESETS,
} from '@/src/game/models/powerups/characterCustomizationOptions'
import { footstepEffectConfigs, footstepEffectTypeKeys, gearItems } from '@/src/game/effects/footstepConfig'
import { motion, useAnimate, Variants } from 'motion/react'
import TextHeadline from '../texts/TextHeadline'
import { IconDiceDisplay, IconRandomDice } from '../icons/IconDiceDisplay'
import {
    playButtonDiceSound,
    playButtonPrimarySound,
    playChangeConfigValueSound,
    playButtonSecondarySound,
    useSlideInWooshSound,
} from '@/src/game/audio/uiSounds'
import { gearNames } from '@/src/game/models/character/characterGear'

type EditorSettingsProps = {
    primaryActionLabel?: string
    onPrimaryAction?: () => void
    onRandomizeAppearance?: () => void
}

const footstepEffectImageByKey: Record<string, string> = {
    dust: '/images/editor/Property 1=Dust.png',
    leaves: '/images/editor/Property 1=Leaf.png',
    glow: '/images/editor/Property 1=Rainbow.png',
}
const colorButtonGradientSources = Array.from(
    new Set(CHAMELEON_TEXTURE_PRESETS.map((preset) => preset.gradient)),
)

const EditorSettings = observer(function EditorSettings({
    onPrimaryAction,
    onRandomizeAppearance,
}: EditorSettingsProps) {
    const gameData = store.getCachedQuery(GetGameDocument)
    const textures = CHAMELEON_TEXTURE_PRESETS
    const equipmentScrollRef = useRef<HTMLDivElement | null>(null)
    const equipmentTouchStartXRef = useRef(0)
    const equipmentTouchStartScrollLeftRef = useRef(0)

    const tabs = useMemo<EditorTab[]>(
        () => [
            { id: 'color', label: gameData?.game?.ConfigScreenColor ?? 'Farbe' },
            { id: 'effect', label: gameData?.game?.ConfigScreenEffect ?? 'Effekt' },
            { id: 'equipment', label: gameData?.game?.ConfigScreenItem ?? 'Ausr�stung' },
        ],
        [gameData],
    )
    const [activeTabId, setActiveTabId] = useState<ConfiguratorTabId>(tabs[0].id as ConfiguratorTabId)
    const [randomizeScope, animateRandomize] = useAnimate()
    const isStele = useSteleBreakpoint()

    useSlideInWooshSound(isStele)

    useEffect(() => {
        store.setConfiguratorActiveTab(activeTabId)
    }, [activeTabId])

    useEffect(() => {
        const preloadLinks: HTMLLinkElement[] = []

        colorButtonGradientSources.forEach((href) => {
            const link = document.createElement('link')
            link.rel = 'preload'
            link.as = 'image'
            link.href = href
            document.head.appendChild(link)
            preloadLinks.push(link)

            const image = new Image()
            image.src = href
        })

        return () => {
            preloadLinks.forEach((link) => link.remove())
        }
    }, [])

    const handleTabChange = (tabId: ConfiguratorTabId) => {
        if (tabId === activeTabId) {
            return
        }

        setActiveTabId(tabId)
        playButtonSecondarySound()
    }

    const handleEquipmentTouchStart = (event: TouchEvent<HTMLDivElement>) => {
        const container = equipmentScrollRef.current

        if (!container) return

        equipmentTouchStartXRef.current = event.touches[0]?.clientX ?? 0
        equipmentTouchStartScrollLeftRef.current = container.scrollLeft
    }

    const handleEquipmentTouchMove = (event: TouchEvent<HTMLDivElement>) => {
        const container = equipmentScrollRef.current
        const currentTouchX = event.touches[0]?.clientX

        if (!container || currentTouchX === undefined) return

        const deltaX = currentTouchX - equipmentTouchStartXRef.current
        container.scrollLeft = equipmentTouchStartScrollLeftRef.current - deltaX
    }

    const handleRandomizeAppearance = async () => {
        playButtonDiceSound()
        onRandomizeAppearance?.()

        await animateRandomize(
            randomizeScope.current,
            {
                y: [0, isStele ? -50 : -25, isStele ? 10 : 5, 0],
                rotate: [0, 210, 360, 360],
            },
            {
                duration: 0.72,
                times: [0, 0.42, 0.78, 1],
                ease: 'easeInOut',
            },
        )
    }

    const headlineVariants: Variants = {
        hidden: {
            opacity: 0,
            y: -100,
            scale: 0,
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: 'spring',
                stiffness: 400,
                damping: 12,
                mass: 0.8,
            },
        },
    }

    return (
        <div
            className='flex flex-col justify-center items-center gap-0 w-full overflow-hidden'
            data-disable-configurator-rotation
        >
            {isStele ? (
                <motion.div variants={headlineVariants} className='z-50 flex flex-col justify-center items-center'>
                    <TextHeadline colorPreset='red' fontSize={isStele ? 192 : 32} leading={1}>
                        Wie ist dein Style?
                    </TextHeadline>
                </motion.div>
            ) : null}

            <img
                src={isStele ? '/images/editor/Overlay.png' : '/images/editor/Overlay.png'}
                alt='bg'
                className={clsx(
                    'absolute',
                    'min-w-126 h-90 -bottom-30',
                    'stele:min-w-810 stele:h-765 stele:-bottom-525',
                )}
            />
            <EditorTabSlider
                activeTabId={activeTabId}
                onTabChange={handleTabChange}
            />

            <>
                {activeTabId === 'color' ? (
                    <div className={clsx(
                        'flex justify-center w-full',
                        'gap-5 max-w-82',
                        'stele:max-w-330 stele:gap-20',
                    )}>
                        {textures.map((color, index) => (
                            <div
                                key={index}
                                className='py-2.5 stele:py-15'
                            >
                                <ColorButton
                                    color={color}
                                    isActive={store.chameleonTexture === color.id}
                                />
                            </div>
                        ))}
                    </div>
                ) : null}

                {activeTabId === 'effect' ? (
                    <div className={clsx(
                        'flex justify-center w-full',
                        'gap-5 max-w-82',
                        'stele:max-w-330 stele:gap-20',
                    )}>
                        {footstepEffectTypeKeys.map((key) => (
                            <div
                                key={key}
                                className='relative py-2.5 stele:py-15'
                            >
                                <EditorButton
                                    alt={footstepEffectConfigs[key].label}
                                    imageClassName='max-w-5 max-h-5 stele:max-w-25 stele:max-h-25'
                                    imageSrc={footstepEffectImageByKey[key]}
                                    isActive={store.footstepEffect === key}
                                    onClick={() => {
                                        if (store.footstepEffect === key) {
                                            return
                                        }

                                        store.setFootstepEffect(key)
                                        playChangeConfigValueSound()
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                ) : null}

                {activeTabId === 'equipment' ? (
                    <div
                        ref={equipmentScrollRef}
                        className='scrollbar-hidden w-full overflow-x-auto overflow-y-hidden touch-pan-x stele:overflow-x-hidden'
                        onTouchStart={isStele ? undefined : handleEquipmentTouchStart}
                        onTouchMove={isStele ? undefined : handleEquipmentTouchMove}
                        style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                        <div className={clsx(
                            'flex justify-center w-max min-w-full',
                            'gap-5 px-5',
                            'stele:gap-20 stele:px-20',
                        )}>
                            {gearNames.slice(0, 3).map((key, index) => (
                                <div key={index} className='py-2.5 stele:py-15'>
                                    <EditorButton
                                        alt={key}
                                        imageSrc={`/images/editor/${key}.png`}
                                        isActive={store.selectedDecorationItems.has(key)}
                                        onClick={() => {
                                            store.toggleDecorationItem(key)
                                            playChangeConfigValueSound()
                                        }}
                                    />
                                </div>
                            ))}

                            {gearItems.map(({ key, gearIndex }) => {
                                const isUnlocked = gearIndex < store.unlockedGearLevel
                                const isSelected = store.selectedGearItems.has(gearIndex)

                                return (
                                    <div key={key} className='py-2.5 stele:py-15'>
                                        <EditorButton
                                            levelIndex={gearIndex}
                                            alt={key}
                                            imageClassName='max-w-9 max-h-9 stele:max-w-40 stele:max-h-40'
                                            imageSrc={`/images/editor/${key}.png`}
                                            isActive={isSelected}
                                            isLocked={!isUnlocked}
                                            onClick={() => {
                                                if (!isUnlocked) {
                                                    return
                                                }

                                                store.toggleGearItem(gearIndex)
                                                playChangeConfigValueSound()
                                            }}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ) : null}
            </>
            <div
                className={clsx(
                    'z-50 flex justify-center items-center w-full',
                    'gap-4 px-4 pt-2',
                    'stele:gap-16 stele:px-16 stele:pt-8',
                )}
            >
                <button type='button' onClick={handleRandomizeAppearance}>
                    <motion.div
                        ref={randomizeScope}
                        className={clsx(
                            'absolute',
                            'pl-0.5',
                            'stele:pl-2',
                        )}
                    >
                        <IconRandomDice width={isStele ? '196' : '49'} height={isStele ? '196' : '49'} />
                    </motion.div>
                    <IconDiceDisplay width={isStele ? '216' : '54'} height={isStele ? '228' : '57'} />
                </button>
                <ButtonCurved
                    label='Lauf!'
                    onClick={() => {
                        playButtonPrimarySound()
                        onPrimaryAction?.()
                    }}
                />
            </div>
        </div>
    )
})

export default EditorSettings

